/**
 * PaymentRequired Component
 * 결제가 필요한 서비스 접근 시 결제 유도 화면
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import * as PortOne from '@portone/browser-sdk/v2';
import { useAuth } from '../../../contexts/AuthContext';
import { usePricing } from '../../../contexts/PricingContext';
import { paymentAPI } from '../../../services/api';
import { PricingCard } from '../PricingCard/PricingCard';
import { ServiceGuideModal } from '../ServiceGuideModal';
import { tokens } from '../../../design-system/tokens';
import { logger } from '../../../utils/logger';
import { isMobile } from '../../../utils/device';

interface PaymentRequiredProps {
  serviceName: string;
  productType: string;
  oneTimePrice: number;
  oneTimeDescription: string;
  oneTimeFeatures: string[];
}

/**
 * 한국어 조사 자동 선택 (을/를)
 * @param word 단어
 * @returns "을" 또는 "를"
 */
const getObjectParticle = (word: string): string => {
  if (!word) return '를';
  const lastChar = word.charAt(word.length - 1);
  const lastCharCode = lastChar.charCodeAt(0);

  // 한글 유니코드 범위: 0xAC00 ~ 0xD7A3
  if (lastCharCode < 0xAC00 || lastCharCode > 0xD7A3) {
    return '를'; // 한글이 아니면 기본값
  }

  // 받침 여부 확인: (코드 - 0xAC00) % 28
  const hasJongseong = (lastCharCode - 0xAC00) % 28 !== 0;
  return hasJongseong ? '을' : '를';
};

// 서비스별 미리보기 이미지 경로 매핑
const SERVICE_PREVIEW_IMAGES: Record<string, string> = {
  today: '/assets/screenshots/today.webp',
  saju: '/assets/screenshots/saju.webp',
  tojung: '/assets/screenshots/tojung.webp',
  compatibility: '/assets/screenshots/compatibility.webp',
  wealth: '/assets/screenshots/wealth.webp',
  love: '/assets/screenshots/love.webp'
};

export const PaymentRequired: React.FC<PaymentRequiredProps> = ({
  serviceName,
  productType,
  oneTimePrice,
  oneTimeDescription,
  oneTimeFeatures
}) => {
  const { user, refreshUserInfo } = useAuth();
  const { subscriptionPrices } = usePricing();
  const [isServiceGuideOpen, setIsServiceGuideOpen] = useState(false);

  const handlePurchase = async (
    purchaseProductType: string,
    amount: number,
    paymentType: 'oneTime' | 'subscription'
  ) => {
    logger.debug('PaymentRequired', { action: 'handlePurchase', purchaseProductType, amount, paymentType });

    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    logger.log('[PaymentRequired] User authenticated');

    try {
      // 1. 결제 생성
      logger.log('[PaymentRequired] Creating payment...');
      const response: any = await paymentAPI.create({
        paymentType,
        productType: purchaseProductType,
        amount
      });
      logger.debug('PaymentRequired', { action: 'createPayment', success: response.success });

      if (response.success) {
        const { merchantUid, productName, amount: paymentAmount } = response.data;

        // 2. returnPath만 sessionStorage에 저장 (redirect 후 원래 페이지로 복귀용)
        // merchantUid는 paymentId로 전달되므로 저장 불필요
        sessionStorage.setItem('payment_returnPath', window.location.pathname);

        // 3. PortOne V2 결제창 호출 (모바일: redirect, 데스크탑: callback)
        logger.log('[PaymentRequired] Calling PortOne.requestPayment...');
        logger.debug('PaymentRequired', {
          action: 'requestPayment',
          merchantUid,
          orderName: productName,
          amount: paymentAmount,
          isMobile: isMobile()
        });

        const portOneResponse = await PortOne.requestPayment({
          storeId: import.meta.env.VITE_PORTONE_STORE_ID,
          channelKey: import.meta.env.VITE_PORTONE_CHANNEL_KEY,
          paymentId: merchantUid,
          orderName: productName,
          totalAmount: paymentAmount,
          currency: 'CURRENCY_KRW',
          payMethod: 'CARD',
          customer: {
            email: user.email || undefined,
            phoneNumber: user.phoneNumber || undefined
          },
          // 모바일에서는 redirect 방식 사용 (redirectUrl 필수)
          ...(isMobile() && {
            redirectUrl: `${window.location.origin}/payment/redirect`
          }),
          // KCP V2 모바일 필수 파라미터
          bypass: {
            kcp_v2: {
              site_name: 'LuckyOn',  // 결제창에 표시될 상호명 (모바일 필수)
              shop_user_id: user.uid  // 리스크 관리용 회원 ID (상품권/휴대폰 결제 시 필수, 카드는 선택)
            }
          } as any
        });

        logger.log('[PaymentRequired] PortOne SDK response received');
        logger.debug('PaymentRequired', { action: 'portOneResponse', hasError: portOneResponse?.code !== undefined });

        // 모바일인 경우 redirect되므로 여기까지 오지 않음
        // 데스크탑(callback 방식)인 경우에만 아래 로직 실행

        // 4. 결제 결과 처리 (데스크탑 callback 방식)
        if (portOneResponse?.code !== undefined) {
          logger.error('[PaymentRequired] Payment failed:', portOneResponse.message);
          sessionStorage.removeItem('payment_merchantUid');
          sessionStorage.removeItem('payment_returnPath');
          alert(`결제 실패: ${portOneResponse.message}`);
          return;
        }

        // 5. 결제 성공 - 검증 및 완료 처리 (데스크탑)
        // PortOne V2 SDK는 성공 시 paymentId를 반환 (우리가 요청 시 보낸 merchantUid)
        const paymentIdFromResponse = portOneResponse?.paymentId;
        logger.debug('PaymentRequired', { action: 'extractPaymentId', hasPaymentId: !!paymentIdFromResponse });

        if (paymentIdFromResponse) {
          try {
            logger.log('[PaymentRequired] Verifying payment...');
            const verifyResponse = await paymentAPI.verify(
              paymentIdFromResponse,
              merchantUid
            );
            logger.debug('PaymentRequired', { action: 'verifyPayment', success: verifyResponse.data.success });

            if (verifyResponse.data.success) {
              logger.log('[PaymentRequired] Completing payment...');
              const completeResponse = await paymentAPI.complete(
                paymentIdFromResponse,
                merchantUid
              );
              logger.debug('PaymentRequired', { action: 'completePayment', success: completeResponse.data.success });

              if (completeResponse.data.success) {
                logger.log('[PaymentRequired] Payment completed successfully');

                // sessionStorage 정리
                sessionStorage.removeItem('payment_returnPath');

                // 사용자 정보를 갱신하여 결제 권한을 업데이트
                logger.log('[PaymentRequired] Refreshing user info...');
                await refreshUserInfo();
                logger.log('[PaymentRequired] User info refreshed');

                // 결제 타입에 따라 적절한 페이지로 이동
                if (paymentType === 'subscription') {
                  // 구독: 현재 서비스 페이지 reload (이제 접근 가능)
                  alert('결제가 완료되었습니다! 서비스를 이용할 수 있습니다.');
                  logger.log('[PaymentRequired] Reloading page after subscription payment');
                  window.location.reload();
                } else {
                  // 일회성: 현재 서비스 페이지 reload (이제 접근 가능)
                  alert('결제가 완료되었습니다! 서비스를 이용할 수 있습니다.');
                  logger.log('[PaymentRequired] Reloading page after one-time payment');
                  window.location.reload();
                }
              } else {
                logger.error('[PaymentRequired] Payment completion failed');
                alert('결제 완료 처리 중 오류가 발생했습니다.');
              }
            } else {
              alert('결제 검증에 실패했습니다.');
            }
          } catch (error: any) {
            logger.error('[PaymentRequired] Payment verification error:', error);
            alert('결제 확인 중 오류가 발생했습니다: ' + error.message);
          }
        } else {
          // paymentId가 response에 없음
          logger.error('[PaymentRequired] No paymentId in response');
          alert('결제 응답에서 결제 ID를 찾을 수 없습니다.');
        }
      } else {
        // 결제 생성 실패
        logger.error('[PaymentRequired] createPayment failed');
        alert('결제 생성에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error: any) {
      logger.error('[PaymentRequired] Payment error:', error.message);
      alert('결제 생성 중 오류가 발생했습니다: ' + error.message);
    }
  };

  return (
    <PageWrapper>
      <Container>
        <TitleSection>
          <Title>{serviceName}{getObjectParticle(serviceName)} 이용하려면 결제가 필요합니다</Title>
          <ServiceGuideLink onClick={() => setIsServiceGuideOpen(true)}>
            서비스 이용안내
          </ServiceGuideLink>
        </TitleSection>
        <Subtitle>원하시는 상품을 선택해주세요</Subtitle>
        <InfoMessage>보셨던 운세는 언제든지 마이페이지에서 다시 확인하실 수 있어요.</InfoMessage>

        <SectionTitle>이 서비스만 이용</SectionTitle>
        <SingleServiceSection>
          <PreviewContainer>
            <PreviewImage
              src={SERVICE_PREVIEW_IMAGES[productType]}
              alt={`${serviceName} 미리보기`}
            />
          </PreviewContainer>
          <PricingCard
            title={serviceName}
            price={oneTimePrice}
            description={oneTimeDescription}
            features={oneTimeFeatures}
            productType={productType}
            paymentType="oneTime"
            onPurchase={() => handlePurchase(productType, oneTimePrice, 'oneTime')}
          />
        </SingleServiceSection>

        <SectionTitle>모든 서비스 이용 (추천)</SectionTitle>
        <SubscriptionSection>
          <PricingCard
            key="1day"
            title="1일 이용권"
            price={subscriptionPrices['1day']}
            description="24시간 모든 서비스 무제한 이용"
            features={['모든 운세 서비스 무제한', '24시간 이용', '영구 보관']}
            productType="1day"
            paymentType="subscription"
            recommended={false}
            onPurchase={() => handlePurchase('1day', subscriptionPrices['1day'], 'subscription')}
          />
          <PricingCard
            key="7days"
            title="7일 이용권"
            price={subscriptionPrices['7days']}
            description="7일간 모든 서비스 무제한 이용"
            features={['모든 운세 서비스 무제한', '7일간 이용', '영구 보관']}
            productType="7days"
            paymentType="subscription"
            recommended={true}
            onPurchase={() => handlePurchase('7days', subscriptionPrices['7days'], 'subscription')}
          />
          <PricingCard
            key="30days"
            title="30일 이용권"
            price={subscriptionPrices['30days']}
            description="한 달간 모든 서비스 무제한 이용"
            features={['모든 운세 서비스 무제한', '30일간 이용', '영구 보관']}
            productType="30days"
            paymentType="subscription"
            recommended={false}
            onPurchase={() => handlePurchase('30days', subscriptionPrices['30days'], 'subscription')}
          />
        </SubscriptionSection>

        <ServiceGuideModal
          isOpen={isServiceGuideOpen}
          onClose={() => setIsServiceGuideOpen(false)}
        />
      </Container>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${tokens.colors.neutral[50]};
  display: flex;
  justify-content: center;
`;

const Container = styled.div`
  width: 600px;
  min-height: 100vh;
  background: ${tokens.colors.neutral[0]};
  padding: ${tokens.spacing[8]} ${tokens.spacing[6]};

  @media (max-width: 600px) {
    width: 100%;
    padding: ${tokens.spacing[6]} ${tokens.spacing[4]};
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${tokens.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${tokens.typography.fontSize['2xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  text-align: center;
  margin-bottom: ${tokens.spacing[2]};
`;

const ServiceGuideLink = styled.button`
  background: none;
  border: none;
  color: ${tokens.colors.neutral[500]};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.regular};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-bottom: ${tokens.spacing[2]};
  transition: color ${tokens.animation.duration.fast} ${tokens.animation.easing.ease};

  &:hover {
    color: ${tokens.colors.primary[500]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
  }
`;

const Subtitle = styled.p`
  font-size: ${tokens.typography.fontSize.base};
  color: ${tokens.colors.neutral[600]};
  text-align: center;
  margin-bottom: ${tokens.spacing[3]};
`;

const InfoMessage = styled.p`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.primary[500]};
  text-align: center;
  margin-bottom: ${tokens.spacing[8]};
  font-weight: ${tokens.typography.fontWeight.medium};
`;

const SectionTitle = styled.h2`
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[4]};
  margin-top: ${tokens.spacing[6]};
`;

const SingleServiceSection = styled.div`
  margin-bottom: ${tokens.spacing[8]};
`;

const PreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${tokens.spacing[6]};

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing[4]};
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: ${tokens.borderRadius.lg};
  box-shadow: ${tokens.shadows.md};
  object-fit: cover;

  @media (max-width: ${tokens.breakpoints.sm}) {
    max-width: 100%;
  }
`;

const SubscriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[4]};
`;
